import { ACCESS_MODULE, type AccessLevel, type AccessModule, type CollaboratorRole } from "./enums";

export type Grants = Record<AccessModule, AccessLevel>;

/**
 * Effective permissions for one person in one wedding. Computed server-side
 * and returned to the client for UX only; the API re-derives it per request
 * and never trusts the client's copy.
 */
export interface EffectiveAccess {
  role: CollaboratorRole;
  isOwner: boolean;
  grants: Grants;
  canSeeFinancials: boolean;
  /** Owner only — invite, revoke, change grants. */
  canManagePeople: boolean;
  /** Owner or co-owner — wedding date, names, venue, budget cap, currency. */
  canEditWeddingCore: boolean;
}

const all = (level: AccessLevel): Grants =>
  Object.fromEntries(ACCESS_MODULE.map((m) => [m, level])) as Grants;

/** Role presets — identical to `CollaboratorRole.defaultGrants` on iOS. */
export function defaultGrants(role: CollaboratorRole): Grants {
  switch (role) {
    case "owner":
    case "coOwner":
      return all("edit");
    case "planner":
      return { ...all("edit"), activity: "view" };
    case "editor":
      return {
        overview: "view", checklist: "edit", guests: "edit",
        budget: "hidden", vendors: "edit", timeline: "edit",
        seating: "edit", notes: "edit", inspiration: "edit",
        shopping: "edit", registry: "view", activity: "view",
      };
    case "viewer":
      return { ...all("view"), budget: "hidden" };
    case "reader":
      // Default for link joiners: a limited read-only peek. Money, guest PII,
      // seating, private notes and the activity feed stay hidden.
      return {
        overview: "view", checklist: "view", guests: "hidden",
        budget: "hidden", vendors: "view", timeline: "view",
        seating: "hidden", notes: "hidden", inspiration: "view",
        shopping: "view", registry: "view", activity: "hidden",
      };
    case "guestHelper":
      return { ...all("hidden"), overview: "view", guests: "edit", seating: "edit", activity: "view" };
    case "custom":
      return all("hidden");
  }
}

export function defaultSeeFinancials(role: CollaboratorRole): boolean {
  return role === "owner" || role === "coOwner" || role === "planner";
}

/** The most restrictive state — used whenever membership is unknown or revoked. */
export const NO_ACCESS: EffectiveAccess = {
  role: "custom",
  isOwner: false,
  grants: all("hidden"),
  canSeeFinancials: false,
  canManagePeople: false,
  canEditWeddingCore: false,
};

export interface MembershipLike {
  role: CollaboratorRole;
  /** Per-module overrides. Missing modules fall back to the role preset. */
  grants: Partial<Grants> | null | undefined;
  seeFinancials: boolean;
  revokedAt: Date | number | string | null | undefined;
}

/**
 * Fail closed: no membership → nothing; revoked → nothing; unknown module
 * key → hidden. Owner is derived from the role, not from a client flag.
 */
export function resolveAccess(membership: MembershipLike | null | undefined): EffectiveAccess {
  if (!membership || membership.revokedAt) return NO_ACCESS;
  const isOwner = membership.role === "owner";
  const preset = defaultGrants(membership.role);
  const grants: Grants = { ...preset };
  if (membership.grants) {
    for (const m of ACCESS_MODULE) {
      const v = membership.grants[m];
      if (v === "hidden" || v === "view" || v === "edit") grants[m] = v;
    }
  }
  if (isOwner) {
    return {
      role: "owner", isOwner: true, grants: all("edit"),
      canSeeFinancials: true, canManagePeople: true, canEditWeddingCore: true,
    };
  }
  return {
    role: membership.role,
    isOwner: false,
    grants,
    canSeeFinancials: membership.seeFinancials,
    canManagePeople: false,
    canEditWeddingCore: membership.role === "coOwner",
  };
}

/**
 * An archived wedding is read-only for everyone: every `edit` grant becomes `view`,
 * wedding details are frozen. The owner keeps people management so they can still
 * remove collaborators, delete or unarchive. Applied by the API's weddingContext;
 * the client mirrors it for UX only.
 */
export function readOnlyAccess(a: EffectiveAccess): EffectiveAccess {
  const grants = { ...a.grants };
  for (const m of ACCESS_MODULE) if (grants[m] === "edit") grants[m] = "view";
  return { ...a, grants, canEditWeddingCore: false };
}

export function canView(a: EffectiveAccess, m: AccessModule): boolean {
  return a.grants[m] === "view" || a.grants[m] === "edit";
}
export function canEdit(a: EffectiveAccess, m: AccessModule): boolean {
  return a.grants[m] === "edit";
}

/** Modules that carry money fields; their amounts are masked server-side. */
export const FINANCIAL_FIELDS: Partial<Record<AccessModule, readonly string[]>> = {
  budget: ["estimatedAmount", "paidAmount"],
  vendors: ["price", "deposit"],
  shopping: ["priceEach"],
  registry: ["price"],
  overview: ["budgetTotal", "categoryBudgets"],
};
